import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

// Web Speech API — supportata su Chrome/Edge/Safari moderni.
const SpeechRecognitionImpl =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
const SPEECH_AVAILABLE = !!SpeechRecognitionImpl;

// ============================================================
// B2C Chatbot — chat AI conversazionale.
// Manda la history a /api/b2c/chat; il backend interroga Claude
// e restituisce { reply, actions[] }. Il frontend esegue le
// actions: applica filtri (URL search params), apre analisi,
// naviga.
// ============================================================

const GREETING = {
  role: 'assistant',
  content:
    "Ciao! Dimmi che casa stai cercando — zona, tipologia, budget — e te la trovo. Puoi anche chiedere cose tipo \"mostrami solo le aste eccezionali\" o \"fammi vedere a Roma sotto 200k\".",
};

const QUICK_PROMPTS = [
  "Cerco bilocale a Roma sotto 150k",
  "Mostrami le aste eccezionali",
  "Fammi vedere le ville in Toscana",
  "Voglio qualcosa a Milano",
];

function Bubble({ from, children }) {
  return (
    <div className={`b2c-chat__msg b2c-chat__msg--${from}`}>
      {from === 'bot' && <div className="b2c-chat__avatar">R</div>}
      <div className="b2c-chat__bubble">{children}</div>
    </div>
  );
}

export default function B2cChat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const scrollerRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputBaseRef = useRef('');

  // Inizializza SpeechRecognition (Web Speech API) una volta
  useEffect(() => {
    if (!SPEECH_AVAILABLE) return;
    const rec = new SpeechRecognitionImpl();
    rec.lang = 'it-IT';
    rec.interimResults = true;
    rec.continuous = true;
    rec.onresult = (e) => {
      let txt = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        txt += e.results[i][0].transcript;
      }
      const base = inputBaseRef.current;
      const sep = base && !base.endsWith(' ') ? ' ' : '';
      setInput((base + sep + txt).trimStart());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    return () => {
      try { rec.stop(); } catch (_) {}
    };
  }, []);

  const toggleMic = () => {
    if (!SPEECH_AVAILABLE) return;
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      try { rec.stop(); } catch (_) {}
      setListening(false);
    } else {
      inputBaseRef.current = input;
      try { rec.start(); setListening(true); }
      catch (_) { setListening(false); }
    }
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const executeActions = (actions = []) => {
    let nextParams = null; // build incrementally if multiple filter actions
    for (const a of actions) {
      if (!a || !a.type) continue;
      switch (a.type) {
        case 'apply_filter': {
          const p = nextParams ?? new URLSearchParams(searchParams);
          for (const [k, v] of Object.entries(a.params || {})) {
            if (v == null || v === '') continue;
            p.set(k, typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v));
          }
          nextParams = p;
          break;
        }
        case 'clear_filter': {
          nextParams = new URLSearchParams();
          break;
        }
        case 'open_immobile': {
          if (a.id) navigate(`/app/immobili/${a.id}`);
          break;
        }
        case 'navigate': {
          const path = a.params?.path;
          if (typeof path === 'string' && path.startsWith('/app/')) navigate(path);
          break;
        }
        default:
          break;
      }
    }
    if (nextParams) setSearchParams(nextParams, { replace: true });
  };

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    const userMsg = { role: 'user', content: trimmed };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const res = await api.post('/api/b2c/chat', { messages: next });
      const reply = res.data?.reply ?? "Non sono riuscito a risponderti.";
      const actions = res.data?.actions ?? [];
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
      executeActions(actions);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: "Mi dispiace, c'è stato un errore. Riprova." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const [expanded, setExpanded] = useState(true);
  // Auto-espandi quando arriva un nuovo messaggio
  useEffect(() => {
    if (messages.length > 1) setExpanded(true);
  }, [messages.length]);

  return (
    <div className={`b2c-chat ${expanded ? 'is-expanded' : 'is-collapsed'}`}>
      <button
        type="button"
        className="b2c-chat__toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-label={expanded ? "Comprimi chat" : "Apri chat"}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2.2"
             strokeLinecap="round" strokeLinejoin="round">
          {expanded
            ? <polyline points="6 9 12 15 18 9" />
            : <polyline points="18 15 12 9 6 15" />
          }
        </svg>
      </button>
      <div className="b2c-chat__scroller" ref={scrollerRef}>
        {messages.map((m, i) => (
          <Bubble key={i} from={m.role === 'user' ? 'user' : 'bot'}>
            {m.content}
          </Bubble>
        ))}
        {sending && (
          <Bubble from="bot">
            <span className="b2c-chat__typing">
              <span /> <span /> <span />
            </span>
          </Bubble>
        )}
      </div>

      <div className="b2c-chat__composer">
        <form
          className="b2c-chat__input-row"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <textarea
            className="b2c-chat__input"
            rows={1}
            placeholder={listening ? "Sto ascoltando…" : "Chiedi qualsiasi cosa…"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            disabled={sending}
          />
          {SPEECH_AVAILABLE && !input.trim() && !sending ? (
            <button
              type="button"
              className={`b2c-chat__mic ${listening ? 'is-listening' : ''}`}
              onClick={toggleMic}
              aria-label={listening ? "Ferma registrazione" : "Parla"}
              title={listening ? "Ferma registrazione" : "Parla"}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.1"
                   strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="2" width="6" height="12" rx="3" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="22" />
              </svg>
            </button>
          ) : (
            <button
              type="submit"
              className="b2c-chat__send"
              disabled={!input.trim() || sending}
              aria-label="Invia"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2"
                   strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
